import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import ReferralBanner from '../../../components/ReferralBanner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiUser, FiMail, FiPhone, FiTag, FiSearch, FiTrash2, FiX, FiArrowLeft, FiTrendingUp, FiInfo, FiPlus } = FiIcons;

const SalesManagement = () => {
  const navigate = useNavigate();
  // SIKRING: Default til tom array hvis sellers mangler
  const { sellers = [], getSalesBySeller, addSeller } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingSeller, setViewingSeller] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSellerError, setAddSellerError] = useState('');

  const generateShortId = () => `SELGER-${Math.floor(1000 + Math.random() * 9000)}`;
  const [newSellerData, setNewSellerData] = useState({ name: '', email: '', phone: '', sellerId: generateShortId(), password: '' });

  // SIKRING: Sjekk at sellers er en array før filter
  const safeSellers = Array.isArray(sellers) ? sellers : [];

  const filteredSellers = safeSellers.filter(s => 
    (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (s.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddSellerError('');
    if (!newSellerData.password) {
      alert("Du må oppgi et passord for selgeren.");
      return;
    }

    const normalizedEmail = newSellerData.email.trim().toLowerCase();
    const emailAlreadyExists = safeSellers.some(
      seller => seller.email?.trim().toLowerCase() === normalizedEmail
    );

    if (emailAlreadyExists) {
      setAddSellerError('Det finnes allerede en selger med denne e-postadressen.');
      return;
    }

    const result = await addSeller({
      id: `seller_${Date.now()}`,
      seller_id: newSellerData.sellerId,
      name: newSellerData.name.trim(),
      email: normalizedEmail,
      phone: newSellerData.phone,
      password: newSellerData.password,
      active: true,
      created_at: new Date().toISOString()
    });

    if (!result?.success) {
      setAddSellerError('Selgeren kunne ikke registreres. Kontroller opplysningene og prøv igjen.');
      return;
    }

    setShowAddModal(false);
    setAddSellerError('');
    setNewSellerData({ name: '', email: '', phone: '', sellerId: generateShortId(), password: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white border-b sticky top-0 z-20 h-16 flex items-center justify-between px-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
          <h1 className="text-xl font-bold">Salgspersonell</h1>
        </div>
        <button onClick={() => { setAddSellerError(''); setShowAddModal(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm">
          <SafeIcon icon={FiPlus} /> Ny Selger
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Søk etter selger, e-post eller ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSellers.map(s => {
            const sId = s.sellerId || s.seller_id || s.id;
            return (
              <motion.div key={s.id} onClick={() => setViewingSeller(s)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 hover:shadow-lg cursor-pointer transition-all group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center font-bold text-xl">
                    {s.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{s.name}</h3>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{sId}</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600"><SafeIcon icon={FiMail} className="text-gray-400" /> {s.email}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-600"><SafeIcon icon={FiPhone} className="text-gray-400" /> {s.phone || 'Ingen tlf'}</div>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Salg: <span className="text-green-600 text-sm ml-1">{getSalesBySeller ? getSalesBySeller(sId).length : 0}</span>
                  </span>
                  <button className="p-2 bg-gray-50 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"><SafeIcon icon={FiInfo} /></button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {viewingSeller && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setViewingSeller(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white w-full max-w-3xl rounded-[32px] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600 p-8 text-white flex justify-between items-start">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-md text-3xl font-bold">
                    {viewingSeller.name.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-3xl font-black">{viewingSeller.name}</h2>
                    <p className="text-white/80 font-bold uppercase text-xs tracking-widest mt-1">ID: {viewingSeller.sellerId || viewingSeller.seller_id}</p>
                  </div>
                </div>
                <button onClick={() => setViewingSeller(null)} className="p-2 hover:bg-white/20 rounded-full">
                  <SafeIcon icon={FiX} size={24} />
                </button>
              </div>
              <div className="p-8 space-y-8">
                <div>
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Delingslenke</h3>
                  <ReferralBanner id={viewingSeller.sellerId || viewingSeller.seller_id} type="seller" name={viewingSeller.name} />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">Registrer Ny Selger</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
              </div>
              <form onSubmit={handleAddSubmit} className="p-6 space-y-4">
                {addSellerError && (
                  <div role="alert" className="text-sm font-medium text-red-600">
                    {addSellerError}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Navn</label>
                  <input type="text" required value={newSellerData.name} onChange={e => setNewSellerData({ ...newSellerData, name: e.target.value })} className="w-full p-3 border rounded-xl" placeholder="Fullt navn" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">E-post</label>
                  <input type="email" required value={newSellerData.email} onChange={e => setNewSellerData({ ...newSellerData, email: e.target.value })} className="w-full p-3 border rounded-xl" placeholder="epost@hmsvarsling.no" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Selger ID (Automatisk)</label>
                  <input type="text" readOnly value={newSellerData.sellerId} className="w-full p-3 bg-gray-50 border rounded-xl font-mono text-blue-600 font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Passord</label>
                  <input type="text" required value={newSellerData.password} onChange={e => setNewSellerData({ ...newSellerData, password: e.target.value })} className="w-full p-3 border rounded-xl" placeholder="Velg passord" />
                </div>
                <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all mt-4">
                  Lagre Selger
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesManagement;
